import { animate } from 'vendor';
import { AnnouncementBarCarousel } from 'theme';

// Minimal resolved animation — used when drag has already positioned the slides correctly.
const INSTANT_ANIM = { finished: Promise.resolve(), finish: () => {} };

AnnouncementBarCarousel.prototype.createOnChangeAnimationControls = function (fromSlide, toSlide, { direction } = {}) {
  const isNext = direction !== 'previous';
  const leaveX = isNext ? '-100%' : '100%';
  const enterX = isNext ? '100%' : '-100%';

  const fromDrag = this._fromDrag;
  this._fromDrag = false;

  return {
    leaveControls: () => {
      if (fromDrag) {
        // fromSlide is already off-screen; clean up inline styles set during drag.
        fromSlide.style.transform = '';
        fromSlide.style.opacity = '0';
        return INSTANT_ANIM;
      }
      return animate(
        fromSlide,
        { opacity: [1, 0], transform: ['translateX(0)', `translateX(${leaveX})`] },
        { duration: 0.3, easing: [0.55, 0.055, 0.675, 0.19] },
      );
    },
    enterControls: () => {
      if (fromDrag) {
        // toSlide is already centered; clean up inline styles set during drag.
        toSlide.style.transform = '';
        toSlide.style.opacity = '';
        toSlide.style.visibility = '';
        return INSTANT_ANIM;
      }
      return animate(
        toSlide,
        { opacity: [0, 1], transform: [`translateX(${enterX})`, 'translateX(0)'] },
        { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] },
      );
    },
  };
};

/**
 * Drag-to-edge swipe behavior:
 * - The current slide follows the pointer.
 * - The next/previous slide appears from the opposite edge in sync.
 * - Reaching the lateral edge (100% of container width) triggers navigation.
 * - Releasing before the edge snaps both slides back to their original positions.
 */
function _setupDragToEdge(carousel) {
  if (carousel._dragSetup) return;
  carousel._dragSetup = true;

  // Base-class listeners fire after ~80px (GestureArea threshold). Remove them.
  carousel.removeEventListener('swipeleft', carousel.next);
  carousel.removeEventListener('swiperight', carousel.previous);

  let startX = 0;
  let isDragging = false;
  let hasTriggered = false;
  let activePointerId = null;
  let companion = null; // the slide entering from the opposite edge
  let companionSide = null; // 'right' (next) or 'left' (previous)
  let companionSavedOpacity = '';
  let companionSavedVisibility = '';
  let dragActivated = false;

  const DRAG_START_THRESHOLD = 8;
  const DRAG_COMMIT_RATIO = 0.28;
  const DRAG_COMMIT_MAX_PX = 220;
  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, summary, [role="button"]';

  carousel.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;

    // Keep desktop click behavior intact for interactive content (links, buttons, inputs).
    if (e.pointerType === 'mouse' && e.target.closest(INTERACTIVE_SELECTOR)) return;

    if (carousel.cells.length <= 1) return;
    startX = e.clientX;
    isDragging = true;
    dragActivated = false;
    hasTriggered = false;
    companion = null;
    companionSide = null;
    companionSavedOpacity = '';
    companionSavedVisibility = '';
    activePointerId = e.pointerId;
  });

  carousel.addEventListener('pointermove', (e) => {
    if (!isDragging || hasTriggered || e.pointerId !== activePointerId) return;

    const deltaX = e.clientX - startX;
    if (!dragActivated) {
      if (Math.abs(deltaX) < DRAG_START_THRESHOLD) return;
      dragActivated = true;
      carousel.setPointerCapture(e.pointerId);
    }

    e.preventDefault();

    const width = carousel.offsetWidth;
    const clamped = Math.max(-width, Math.min(width, deltaX));
    const slide = carousel.selectedCell;
    if (!slide) return;

    // Resolve companion on first significant movement.
    if (!companion && Math.abs(clamped) > 2) {
      if (clamped < 0) {
        // Dragging left → next slide enters from the right.
        const nextIndex = (carousel.selectedIndex + 1) % carousel.cells.length;
        companion = carousel.cells[nextIndex];
        companionSide = 'right';
      } else {
        // Dragging right → previous slide enters from the left.
        const prevIndex = (carousel.selectedIndex - 1 + carousel.cells.length) % carousel.cells.length;
        companion = carousel.cells[prevIndex];
        companionSide = 'left';
      }
      companionSavedOpacity = companion.style.opacity;
      companionSavedVisibility = companion.style.visibility;
    }

    // Move current slide.
    slide.style.transform = `translateX(${clamped}px)`;

    // Move companion in sync from the opposite edge.
    // When clamped = ±width, companionOffset = 0 (slide is centered).
    if (companion) {
      const companionOffset = companionSide === 'right' ? clamped + width : clamped - width;
      companion.style.visibility = 'visible';
      companion.style.opacity = '1';
      companion.style.transform = `translateX(${companionOffset}px)`;
    }
  });

  const endDrag = (e) => {
    if (!isDragging || e.pointerId !== activePointerId) return;
    isDragging = false;
    activePointerId = null;

    if (!dragActivated) return;
    dragActivated = false;

    const slide = carousel.selectedCell;
    if (!slide) return;

    const width = carousel.offsetWidth;
    const currentDeltaX = parseFloat(slide.style.transform.match(/translateX\((-?[\d.]+)px\)/)?.[1] || '0');
    const threshold = Math.min(width * DRAG_COMMIT_RATIO, DRAG_COMMIT_MAX_PX);
    const goNext = currentDeltaX < 0;

    if (Math.abs(currentDeltaX) > threshold && companion) {
      // Threshold crossed → animate both slides to completion, then trigger navigation.
      hasTriggered = true;
      const slideTarget = goNext ? -width : width;
      const companionCurrentOffset = parseFloat(
        companion.style.transform.match(/translateX\((-?[\d.]+)px\)/)?.[1] || '0',
      );

      const slideAnim = animate(
        slide,
        { transform: [`translateX(${currentDeltaX}px)`, `translateX(${slideTarget}px)`] },
        { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] },
      );
      const companionAnim = animate(
        companion,
        { transform: [`translateX(${companionCurrentOffset}px)`, 'translateX(0px)'] },
        { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] },
      );

      Promise.all([slideAnim.finished, companionAnim.finished]).then(() => {
        carousel._fromDrag = true;
        if (goNext) {
          carousel.next();
        } else {
          carousel.previous();
        }
      });
    } else {
      // Threshold not crossed → snap both back to original positions.
      if (currentDeltaX !== 0) {
        animate(
          slide,
          { transform: [`translateX(${currentDeltaX}px)`, 'translateX(0px)'] },
          { duration: 0.35, easing: [0.215, 0.61, 0.355, 1] },
        ).finished.finally(() => {
          slide.style.transform = '';
        });
      }

      if (companion) {
        const companionCurrentOffset = parseFloat(
          companion.style.transform.match(/translateX\((-?[\d.]+)px\)/)?.[1] || '0',
        );
        const companionTarget = companionSide === 'right' ? width : -width;
        const savedOpacity = companionSavedOpacity;
        const savedVisibility = companionSavedVisibility;
        const companionRef = companion;
        animate(
          companion,
          { transform: [`translateX(${companionCurrentOffset}px)`, `translateX(${companionTarget}px)`] },
          { duration: 0.35, easing: [0.215, 0.61, 0.355, 1] },
        ).finished.finally(() => {
          companionRef.style.transform = '';
          companionRef.style.opacity = savedOpacity;
          companionRef.style.visibility = savedVisibility;
        });
        companion = null;
        companionSide = null;
      }
    }
  };

  carousel.addEventListener('pointerup', endDrag);
  carousel.addEventListener('pointercancel', endDrag);
  // lostpointercapture fires when the browser releases capture (e.g. pointer leaves
  // the viewport to the browser chrome). Acts as a final safety net.
  carousel.addEventListener('lostpointercapture', endDrag);
}

// Patch connectedCallback for instances created after this module loads (e.g. Shopify editor).
const _origConnectedCallback = AnnouncementBarCarousel.prototype.connectedCallback;
AnnouncementBarCarousel.prototype.connectedCallback = function () {
  _origConnectedCallback?.call(this);
  _setupDragToEdge(this);
};

// This module is deferred, so the DOM is already parsed and elements already upgraded by theme.js.
// Initialize any instances that are already in the DOM right now.
document.querySelectorAll('announcement-bar-carousel').forEach(_setupDragToEdge);

// Prevent scroll triggered by facet filter updates.
// theme.js schedules scrollIntoView via requestIdleCallback AFTER the async fetch completes,
// so timeout-based suppression fires too early. Instead, we lock the scroll position
// by listening to the scroll event and instantly restoring whenever a scroll is attempted.
let _facetScrollY = null;
let _facetScrollRestoring = false;

const facetScrollLockHandler = () => {
  if (_facetScrollRestoring || _facetScrollY === null) return;
  _facetScrollRestoring = true;
  window.scrollTo({ top: _facetScrollY, left: 0, behavior: 'instant' });
  requestAnimationFrame(() => {
    _facetScrollRestoring = false;
  });
};

// Keep facets-panel-drawer open across facet:update AJAX section replacements.
//
// Root cause: FacetsPanelDrawer extends FacetsDrawer (Drawer / DialogElement) and has
// shouldAppendToBody = true. When the panel is open it lives at document.body, NOT inside
// the section. After the async fetch theme.js does:
//   1. shopifySection.replaceChildren(...)   → inserts a fresh facets-panel-drawer in the section
//   2. document.getElementById(focusedEl.id).focus()  → finds the same checkbox ID in the NEW
//      panel (inside the section), calls focus() on it, which is OUTSIDE the live FocusTrap
//      bound to the OLD panel at body → FocusTrap deactivates → onDeactivate → hide() → panel closes
//
// Fix:
//   A) Synchronously remove the ID from the currently-focused element so theme.js sees
//      focusedEl.id === "" and skips the .focus() call entirely.
//   B) After replaceChildren (MutationObserver), transplant the fresh light-DOM children
//      from the new panel into the live panel (still at body), then remove the duplicate.
(function () {
  // Tracks { element, savedId } for each in-flight facet:update, keyed by sectionId.
  // Multiple events may fire when the user clicks quickly (aborting the previous fetch).
  const _pending = new Map();

  document.addEventListener('facet:update', (event) => {
    const sectionId = event.detail.url.searchParams.get('section_id');
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    // Restore any IDs that were neutralized by a previous (now-aborted) event.
    for (const [, saved] of _pending) {
      if (saved.element && saved.savedId) saved.element.setAttribute('id', saved.savedId);
    }
    _pending.clear();

    // The open panel is at document.body (moved there by DialogElement.show() because
    // shouldAppendToBody = true). Fall back to section lookup for the closed/mobile case.
    const openPanel =
      document.body.querySelector(':scope > facets-panel-drawer[open]') ??
      section.querySelector('facets-panel-drawer[open]');
    if (!openPanel) return;

    // (A) Neutralize focused element's ID so theme.js skips the .focus() call.
    const focused = document.activeElement;
    if (focused && focused.id && openPanel.contains(focused)) {
      _pending.set(sectionId, { element: focused, savedId: focused.id });
      focused.removeAttribute('id');
    }

    // (B) After replaceChildren, transplant fresh panel content into the live panel.
    const observer = new MutationObserver(() => {
      observer.disconnect();

      // Restore neutralized ID (if it hasn't been restored by a later event already).
      const saved = _pending.get(sectionId);
      if (saved) {
        if (saved.element && saved.savedId) saved.element.setAttribute('id', saved.savedId);
        _pending.delete(sectionId);
      }

      const newPanel = section.querySelector('facets-panel-drawer');
      if (!newPanel || newPanel === openPanel) return;

      // Focus the panel container BEFORE replacing its children.
      // replaceChildren removes all old children first; if a child has focus, the browser
      // moves focus to <body> at that moment — before the new children are inserted.
      // FocusTrap's focusin handler fires, finds no focusable elements inside the trap,
      // and may call onDeactivate → hide() — closing the panel.
      // Focusing openPanel itself (the trap container) keeps focus on a stable node that
      // survives replaceChildren, preventing FocusTrap from deactivating.
      if (!openPanel.hasAttribute('tabindex')) openPanel.setAttribute('tabindex', '-1');
      openPanel.focus();

      // Move fresh light-DOM children into the live panel (which is open at body).
      // Array.from() avoids live-NodeList mutation issues during the move.
      openPanel.replaceChildren(...Array.from(newPanel.childNodes));
      // Remove the now-empty placeholder that replaceChildren inserted in the section.
      newPanel.remove();

      // On close, DialogElement reattaches the panel to its _originalParentBeforeAppend,
      // which still points to the OLD .collection div (now detached by replaceChildren).
      // The panel ends up detached → connectedCallback never re-binds the toggle delegate
      // and the "Filters" button can no longer reopen it. Reattach to the live section.
      if (!openPanel.__postCloseReattachBound) {
        openPanel.__postCloseReattachBound = true;
        openPanel.addEventListener('dialog:after-hide', () => {
          if (document.contains(openPanel)) return;
          const liveSection = document.getElementById(`shopify-section-${sectionId}`);
          const liveCollection = liveSection?.querySelector('.collection');
          if (liveCollection) liveCollection.insertBefore(openPanel, liveCollection.firstChild);
        });
      }
    });
    observer.observe(section, { childList: true });
  });
})();

// FacetClearButton: dispatches facet:update without relying on extractSectionId(event.target).
// Uses the section-id attribute (set via Liquid) to avoid DOM traversal failures
// caused by dialog-element moving content to document.body on mobile.
class FacetClearButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      event.preventDefault();
      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return;
      // Reset checkboxes immediately for instant visual feedback.
      // document.querySelectorAll('facets-form form').forEach((form) => form.reset());
      document
        .querySelectorAll('facets-form form .facets-panel input[type="checkbox"]')
        .forEach((checkbox) => (checkbox.checked = false));
      const url = new URL(link.href);
      url.searchParams.set('section_id', sectionId);
      this.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
    });
  }
}
if (!customElements.get('facet-clear-button')) {
  customElements.define('facet-clear-button', FacetClearButton);
}

document.addEventListener('facet:update', (event) => {
  const paginationRel = event.target?.querySelector?.('a')?.getAttribute('rel');

  if (paginationRel === 'next' || paginationRel === 'prev') return;

  _facetScrollY = window.scrollY;
  window.addEventListener('scroll', facetScrollLockHandler, { passive: true });

  // Remove the lock after all async scroll attempts have had time to fire.
  // Worst case: fetch (~500ms) + requestIdleCallback deadline (500ms) = ~1000ms.
  setTimeout(() => {
    window.removeEventListener('scroll', facetScrollLockHandler);
    _facetScrollY = null;
    _facetScrollRestoring = false;
  }, 1500);
});
